<template>
    <section dir="rtl" class="relative py-20 md:py-28 bg-white overflow-hidden">
        <div class="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-brand-blue-10 blur-3xl" />
        <div class="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-brand-cyan-10 blur-3xl" />
        <div class="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-brand-blue-5 blur-3xl" />

        <div class="relative mx-auto max-w-7xl px-5 sm:px-8">
            <div class="text-center max-w-3xl mx-auto">
                <span class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-blue-10 text-brand-blue text-sm font-bold">
                    <lucide-icon name="Bell" class="h-4 w-4" /> تابع يوميًا
                </span>
                <h2 class="mt-4 text-3xl md:text-5xl font-extrabold text-brand-navy leading-tight">
                    تابعنا <span class="relative inline-block">
                        <span class="relative z-10 follow-daily-highlight">مجانًا</span>
                        <span class="follow-daily-highlight-mark" />
                    </span> وخليك جاهز قبل الامتحان
                </h2>
                <p class="mt-4 text-base md:text-lg text-brand-navy-65 leading-relaxed">
                    اشترك في قنوات البرمجة مع غنيم عشان توصلك الشروحات، المراجعات، الملفات، التنبيهات، وأهم تدريبات Programming &amp; AI أول بأول.
                </p>
            </div>

            <div class="relative mt-16">
                <div class="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-[420px] rounded-full follow-daily-glow blur-[110px] -z-0" />

                <div class="follow-daily-layout relative">
                    <div class="group follow-daily-card-youtube relative z-10 h-full flex flex-col overflow-hidden rounded-[2rem] bg-white border border-red-100 p-6 md:p-8 shadow-[0_20px_50px_-20px_rgba(239,68,68,0.18)] hover:shadow-[0_25px_60px_-15px_rgba(239,68,68,0.30)] hover:-translate-y-1 transition-all duration-500">
                        <div class="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-red-500 bg-opacity-10 blur-3xl group-hover:bg-opacity-20 transition-colors" />

                        <div class="follow-daily-card-header relative">
                            <div class="follow-daily-card-icon follow-daily-card-icon--youtube">
                                <lucide-icon name="Youtube" :size="28" class="fill-white" />
                            </div>
                            <div class="follow-daily-card-header-text">
                                <div class="text-[11px] font-bold text-red-500 tracking-[0.2em]">YOUTUBE</div>
                                <h3 class="text-xl md:text-2xl font-extrabold text-brand-navy mt-0.5 leading-snug">
                                    قناة البرمجة مع غنيم على YouTube
                                </h3>
                            </div>
                        </div>

                        <p class="follow-daily-card-desc relative mt-4 text-brand-navy-70 leading-relaxed text-[15px]">
                            فيديوهات شرح، مراجعات، حل أسئلة، أخطاء شائعة، وتدريبات تساعدك تفهم Programming &amp; AI بطريقة أسهل.
                        </p>

                        <div class="relative mt-5 rounded-2xl overflow-hidden border border-brand-navy-10 shadow-md bg-white">
                            <img
                                :src="youtubeThumbsImage"
                                alt="فيديوهات قناة البرمجة مع غنيم على YouTube"
                                loading="lazy"
                                class="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-700"
                            />
                            <div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-white-40 via-transparent to-transparent" />
                            <div class="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold shadow-lg">
                                <span class="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                                جديد
                            </div>
                        </div>

                        <div class="relative mt-5 grid grid-cols-3 gap-2">
                            <div
                                v-for="s in youtubeStats"
                                :key="s.l"
                                class="rounded-xl follow-daily-stat border border-red-100 px-2 py-2.5 text-center"
                            >
                                <div class="text-sm font-extrabold text-red-600">{{ s.v }}</div>
                                <div class="text-[10px] text-brand-navy-60 mt-0.5">{{ s.l }}</div>
                            </div>
                        </div>

                        <div class="follow-daily-tags relative mt-5">
                            <span
                                v-for="t in youtubeTags"
                                :key="t"
                                class="text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600 border border-red-100"
                            >{{ t }}</span>
                        </div>

                        <a
                            :href="YOUTUBE_URL"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="follow-daily-cta relative mt-auto w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-l from-red-600 to-red-500 text-white font-bold shadow-lg shadow-[0_10px_15px_-3px_rgba(239,68,68,0.3)] hover:shadow-[0_10px_15px_-3px_rgba(239,68,68,0.5)] hover:scale-[1.02] transition-all"
                        >
                            <lucide-icon name="Youtube" :size="20" class="fill-white text-white" />
                            اشترك في القناة
                            <lucide-icon name="ExternalLink" :size="14" class="follow-daily-cta-link" />
                        </a>
                    </div>

                    <div class="follow-daily-portrait">
                        <svg class="absolute inset-x-0 bottom-0 w-full h-[120%] text-brand-blue-25 pointer-events-none" viewBox="0 0 200 200" fill="none" aria-hidden="true">
                            <circle cx="100" cy="160" r="86" stroke="currentColor" stroke-width="0.4" stroke-dasharray="3 3" />
                            <circle cx="100" cy="160" r="64" stroke="currentColor" stroke-width="0.4" stroke-dasharray="2 2" />
                            <circle cx="100" cy="160" r="42" stroke="currentColor" stroke-width="0.4" stroke-dasharray="2 4" />
                        </svg>

                        <div class="absolute inset-0 m-auto h-[440px] w-[440px] rounded-full bg-gradient-to-br from-brand-blue-25 via-brand-cyan-15 to-transparent blur-3xl" />
                        <div class="absolute bottom-2 left-1/2 -translate-x-1/2 h-8 w-80 rounded-full bg-brand-blue-20 blur-2xl" />

                        <img
                            :src="followImage"
                            alt="محمد غنيم — البرمجة مع غنيم"
                            loading="lazy"
                            decoding="async"
                            class="absolute bottom-0 left-1/2 -translate-x-1/2 h-[660px] w-auto max-w-none object-contain object-bottom drop-shadow-[0_40px_60px_rgba(37,99,235,0.35)]"
                        />

                        <div class="pointer-events-auto absolute top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-white-95 backdrop-blur-md border border-brand-blue-15 px-4 py-2 rounded-full shadow-xl shadow-brand-blue-10">
                            <span class="relative flex h-2 w-2">
                                <span class="absolute inset-0 rounded-full bg-brand-cyan animate-ping opacity-60" />
                                <span class="relative h-2 w-2 rounded-full bg-brand-cyan" />
                            </span>
                            <span class="text-xs font-extrabold text-brand-navy">تابعنا هنا</span>
                        </div>

                        <div class="follow-daily-bubble-youtube pointer-events-auto absolute top-[7.5rem] bg-white rounded-2xl rounded-bl-sm border border-red-200 border-opacity-50 shadow-xl px-3 py-1.5 text-[11px] font-bold text-red-600 whitespace-nowrap animate-float">
                            <lucide-icon name="Youtube" :size="14" class="inline ml-1 fill-red-500 text-red-500" />
                            فيديو جديد
                        </div>
                        <div class="follow-daily-bubble-telegram pointer-events-auto absolute top-[7.5rem] bg-white rounded-2xl rounded-br-sm border border-[rgba(34,158,217,0.25)] shadow-xl px-3 py-1.5 text-[11px] font-bold text-[#229ED9] whitespace-nowrap animate-float-slow">
                            <lucide-icon name="Send" :size="14" class="inline ml-1" />
                            لايف الليلة
                        </div>
                    </div>

                    <div class="group follow-daily-card-telegram relative z-10 h-full flex flex-col overflow-hidden rounded-[2rem] bg-white border border-[rgba(34,158,217,0.15)] p-6 md:p-8 shadow-[0_20px_50px_-20px_rgba(34,158,217,0.18)] hover:shadow-[0_25px_60px_-15px_rgba(34,158,217,0.30)] hover:-translate-y-1 transition-all duration-500">
                        <div class="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full bg-[#229ED9] bg-opacity-10 blur-3xl group-hover:bg-opacity-20 transition-colors" />

                        <div class="follow-daily-card-header relative">
                            <div class="follow-daily-card-icon follow-daily-card-icon--telegram">
                                <lucide-icon name="Send" :size="24" />
                            </div>
                            <div class="follow-daily-card-header-text">
                                <div class="text-[11px] font-bold text-[#229ED9] tracking-[0.2em]">TELEGRAM</div>
                                <h3 class="text-xl md:text-2xl font-extrabold text-brand-navy mt-0.5 leading-snug">
                                    انضم لقناة البرمجة مع غنيم
                                </h3>
                            </div>
                        </div>

                        <p class="follow-daily-card-desc relative mt-4 text-brand-navy-70 leading-relaxed text-[15px]">
                            تابع التنبيهات، الملفات، المراجعات، روابط الفيديوهات، وأهم التحديثات أول بأول.
                        </p>

                        <div class="relative mt-5 rounded-2xl overflow-hidden border border-[rgba(34,158,217,0.2)] shadow-md bg-gradient-to-br from-[#EAF6FB] to-white">
                            <div class="flex items-center gap-2 px-3 py-2 bg-white border-b border-[rgba(34,158,217,0.1)]">
                                <div class="follow-daily-chat-avatar">
                                    <lucide-icon name="Send" :size="14" />
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="text-[12px] font-bold text-brand-navy truncate">البرمجة مع غنيم</div>
                                    <div class="text-[10px] text-brand-navy-55">قناة • تحديثات يومية</div>
                                </div>
                                <span class="flex items-center gap-1 text-[10px] font-bold text-[#229ED9] bg-[rgba(34,158,217,0.1)] px-2 py-0.5 rounded-full">
                                    <span class="h-1.5 w-1.5 rounded-full bg-[#229ED9] animate-pulse" /> ONLINE
                                </span>
                            </div>

                            <div class="follow-daily-chat-messages p-4 space-y-2.5">
                                <div class="flex justify-start">
                                    <div class="max-w-[80%] bg-white text-brand-navy text-sm rounded-2xl rounded-tr-sm px-4 py-2 shadow-sm border border-brand-navy-5">
                                        📘 ملف تدريبات الوحدة الثانية متاح الآن
                                        <div class="text-[10px] text-brand-navy-40 mt-1 text-left">10:32</div>
                                    </div>
                                </div>
                                <div class="flex justify-start">
                                    <div class="max-w-[85%] bg-gradient-to-l from-[#2AABEE] to-[#229ED9] text-white text-sm rounded-2xl rounded-tr-sm px-4 py-2 shadow-sm">
                                        🔴 لايف المراجعة النهاردة 8 مساءً — متفوّتوش
                                        <div class="text-[10px] text-white-70 mt-1 text-left">11:05</div>
                                    </div>
                                </div>
                                <div class="flex justify-start">
                                    <div class="max-w-[75%] bg-white text-brand-navy text-sm rounded-2xl rounded-tr-sm px-4 py-2 shadow-sm border border-brand-navy-5">
                                        ⏰ فاضل 3 أيام على الامتحان
                                        <div class="text-[10px] text-brand-navy-40 mt-1 text-left">14:18</div>
                                    </div>
                                </div>
                                <div class="flex justify-start">
                                    <div class="max-w-[70%] bg-white text-brand-navy text-sm rounded-2xl rounded-tr-sm px-4 py-2 shadow-sm border border-brand-navy-5">
                                        سؤال اليوم: Loops في JavaScript
                                        <div class="text-[10px] text-brand-navy-40 mt-1 text-left">16:40</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="follow-daily-tags relative mt-5">
                            <span
                                v-for="t in telegramTags"
                                :key="t"
                                class="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[rgba(34,158,217,0.1)] text-[#229ED9] border border-[rgba(34,158,217,0.15)]"
                            >{{ t }}</span>
                        </div>

                        <a
                            :href="TELEGRAM_URL"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="follow-daily-cta relative mt-auto w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-l from-[#2AABEE] to-[#229ED9] text-white font-bold shadow-lg shadow-[rgba(34,158,217,0.3)] hover:shadow-[0_10px_15px_-3px_rgba(34,158,217,0.5)] hover:scale-[1.02] transition-all"
                        >
                            <lucide-icon name="Send" :size="20" />
                            انضم الآن
                            <lucide-icon name="ExternalLink" :size="14" class="follow-daily-cta-link" />
                        </a>
                    </div>
                </div>
            </div>

            <div class="mt-10 flex flex-wrap items-center justify-center gap-3">
                <a
                    :href="WHATSAPP_CHANNEL_URL"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-2 rounded-full border border-[#25D366] bg-white px-4 py-2 text-sm font-bold text-[#128C7E]"
                >
                    قناة واتساب
                </a>
                <a
                    :href="ALL_LINKS_URL"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-2 rounded-full border border-brand-navy-10 bg-white px-4 py-2 text-sm font-bold text-brand-navy"
                >
                    كل الروابط
                </a>
            </div>
            <div class="mt-16 text-center">
                <p class="text-lg md:text-xl font-bold text-brand-navy">
                    ابدأ مجانًا… ولما تكون جاهز،
                    <span class="follow-daily-closing-gradient">
                        ادخل المنظومة الكاملة.
                    </span>
                </p>
            </div>
        </div>
    </section>
</template>

<script>
import LucideIcon from './LucideIcon.vue';
import { ALL_LINKS_URL, TELEGRAM_URL, WHATSAPP_CHANNEL_URL, YOUTUBE_URL } from './landing-data';

export default {
    name: 'WelcomeFollowDaily',
    components: { LucideIcon },
    data() {
        return {
            followImage: require('@/assets/welcome/ghonaim-follow.webp'),
            youtubeThumbsImage: require('@/assets/welcome/youtube-thumbnails.webp'),
            YOUTUBE_URL,
            TELEGRAM_URL,
            WHATSAPP_CHANNEL_URL,
            ALL_LINKS_URL,
            youtubeStats: [
                { v: '+2M', l: 'مشاهدة' },
                { v: '+130', l: 'فيديو' },
                { v: 'متنوّع', l: 'محتوى' },
            ],
            youtubeTags: ['شرح', 'مراجعات', 'حل أسئلة', 'Coding Lab', 'Live', 'نصائح'],
            telegramTags: ['مراجعة جديدة', 'ملف تدريبات', 'رابط اللايف', 'تنبيه الامتحان'],
        };
    },
};
</script>
